import React, { useState } from "react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calculator, TrendingUp, Info, Banknote } from "lucide-react";

export const LumpsumCalculatorPage = () => {
  const [investment, setInvestment] = useState(100000);
  const [years, setYears] = useState(10);
  const [expectedReturn, setExpectedReturn] = useState(12);
  const [result, setResult] = useState(null);

  const calculateLumpsum = () => {
    const P = investment;
    const r = expectedReturn / 100;
    const n = years;
    
    const futureValue = P * Math.pow(1 + r, n);
    const gains = futureValue - P;
    
    setResult({
      totalInvested: P,
      estimatedReturns: Math.round(gains),
      totalValue: Math.round(futureValue),
      absoluteReturns: ((gains / P) * 100).toFixed(1),
      cagr: expectedReturn
    });
  };

  const formatCurrency = (num) => {
    if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
    return `₹${num.toLocaleString('en-IN')}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h1 className="font-serif text-3xl sm:text-4xl font-bold mb-4">
              Lumpsum <span className="gold-text">Calculator</span>
            </h1>
            <p className="text-muted-foreground">
              Calculate the future value of your one-time investment
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Banknote className="w-5 h-5 text-primary" />
                  Investment Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>Investment Amount</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                    <Input
                      type="number"
                      value={investment}
                      onChange={(e) => setInvestment(Number(e.target.value))}
                      className="pl-8"
                      data-testid="lumpsum-amount-input"
                    />
                  </div>
                  <input
                    type="range"
                    min="10000"
                    max="10000000"
                    step="10000"
                    value={investment}
                    onChange={(e) => setInvestment(Number(e.target.value))}
                    className="w-full accent-primary"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Investment Period (Years)</Label>
                  <Input
                    type="number"
                    value={years}
                    onChange={(e) => setYears(Number(e.target.value))}
                    min="1"
                    max="40"
                    data-testid="lumpsum-years-input"
                  />
                  <input
                    type="range"
                    min="1"
                    max="40"
                    value={years}
                    onChange={(e) => setYears(Number(e.target.value))}
                    className="w-full accent-primary"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Expected Annual Return (%)</Label>
                  <Input
                    type="number"
                    value={expectedReturn}
                    onChange={(e) => setExpectedReturn(Number(e.target.value))}
                    min="1"
                    max="30"
                    step="0.5"
                    data-testid="lumpsum-return-input"
                  />
                  <input
                    type="range"
                    min="1"
                    max="30"
                    step="0.5"
                    value={expectedReturn}
                    onChange={(e) => setExpectedReturn(Number(e.target.value))}
                    className="w-full accent-primary"
                  />
                </div>

                <Button onClick={calculateLumpsum} className="w-full" data-testid="calculate-lumpsum-btn">
                  <Calculator className="w-4 h-4 mr-2" />
                  Calculate Returns
                </Button>
              </CardContent>
            </Card>

            <div className="space-y-6">
              {result && (
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-green-500" />
                      Investment Growth
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 rounded-lg bg-secondary">
                        <p className="text-sm text-muted-foreground">Invested Amount</p>
                        <p className="text-xl font-bold text-foreground">{formatCurrency(result.totalInvested)}</p>
                      </div>
                      <div className="p-4 rounded-lg bg-green-500/10">
                        <p className="text-sm text-muted-foreground">Est. Returns</p>
                        <p className="text-xl font-bold text-green-500">{formatCurrency(result.estimatedReturns)}</p>
                      </div>
                    </div>
                    
                    <div className="p-6 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20">
                      <p className="text-sm text-muted-foreground mb-1">Future Value</p>
                      <p className="text-3xl font-bold gold-text">{formatCurrency(result.totalValue)}</p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Absolute Returns: <span className="text-green-500 font-medium">{result.absoluteReturns}%</span>
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Principal</span>
                        <span>Returns</span>
                      </div>
                      <div className="h-4 rounded-full overflow-hidden bg-secondary flex">
                        <div 
                          className="bg-primary/60 h-full"
                          style={{ width: `${(result.totalInvested / result.totalValue) * 100}%` }}
                        />
                        <div 
                          className="bg-green-500 h-full"
                          style={{ width: `${(result.estimatedReturns / result.totalValue) * 100}%` }}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card className="bg-card border-border">
                <CardContent className="pt-6">
                  <div className="flex gap-3">
                    <Info className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <div className="text-sm text-muted-foreground space-y-2">
                      <p><strong>Lumpsum Investment:</strong> A one-time investment where you invest a large amount at once.</p>
                      <p><strong>Power of Compounding:</strong> Your returns earn returns, accelerating wealth creation.</p>
                      <p><strong>Risk Note:</strong> Market timing matters for lumpsum. Consider STP for large amounts.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
